import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const UploadReportModal = ({ show, onClose, onSuccess, companies }) => {
    const [formData, setFormData] = useState({
        employeeId: '',
        companyId: '',
        branchId: '',
        totalSales: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        day: new Date().getDate()
    });
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Loaded once per time the modal opens — used only to populate the optional Branch
    // override dropdown once a company is picked.
    useEffect(() => {
        if (!show) return;
        const fetchBranches = async () => {
            try {
                const res = await axiosInstance.get('/api/sales-target/organizations/tree');
                if (res.data.success) {
                    const flattenBranches = (nodes) => {
                        let list = [];
                        nodes.forEach(n => {
                            if (n.type === 'branch') list.push(n);
                            if (n.children) list = [...list, ...flattenBranches(n.children)];
                        });
                        return list;
                    };
                    setBranches(flattenBranches(res.data.data));
                }
            } catch {
                // Silently ignore — the branch override is optional, so a failed fetch just
                // leaves the dropdown empty and reports fall back to the salesman's own branch.
            }
        };
        fetchBranches();
    }, [show]);

    const branchesForCompany = branches.filter(b => b.parentId === formData.companyId);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                employeeId: formData.employeeId.toUpperCase(),
                companyId: formData.companyId,
                totalSales: parseFloat(formData.totalSales),
                year: parseInt(formData.year),
                month: parseInt(formData.month),
                day: parseInt(formData.day)
            };
            // Only sent when explicitly chosen — omitting it lets the server fall back to
            // the salesman's own current branch, exactly like before this field existed.
            if (formData.branchId) payload.branchId = formData.branchId;

            const response = await axiosInstance.post('/api/sales-target/reports', payload);

            if (response.data.success) {
                onSuccess();
                handleClose();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload report');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            employeeId: '',
            companyId: '',
            branchId: '',
            totalSales: '',
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            day: new Date().getDate()
        });
        setError(null);
        onClose();
    };

    const getMonthName = (month) => {
        return new Date(2024, month - 1).toLocaleString('default', { month: 'long' });
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Upload Sales Report</h2>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Year *</label>
                            <select
                                value={formData.year}
                                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                required
                            >
                                {[2024, 2025, 2026].map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Month *</label>
                            <select
                                value={formData.month}
                                onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                                required
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                    <option key={month} value={month}>
                                        {getMonthName(month)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Day *</label>
                            <select
                                value={formData.day}
                                onChange={(e) => setFormData({ ...formData, day: parseInt(e.target.value) })}
                                required
                            >
                                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                    <option key={day} value={day}>{day}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Company *</label>
                        <select
                            value={formData.companyId}
                            onChange={(e) => setFormData({ ...formData, companyId: e.target.value, branchId: '' })}
                            required
                        >
                            <option value="">-- Select Company --</option>
                            {companies.map(company => (
                                <option key={company._id} value={company._id}>
                                    {company.name}
                                </option>
                            ))}
                        </select>
                        <small>Select the company the salesman belongs to</small>
                    </div>

                    <div className="form-group">
                        <label>Branch (optional)</label>
                        <select
                            value={formData.branchId}
                            onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                            disabled={!formData.companyId}
                        >
                            <option value="">-- Use salesman's current branch --</option>
                            {branchesForCompany.map(branch => (
                                <option key={branch._id} value={branch._id}>
                                    {branch.name}
                                </option>
                            ))}
                        </select>
                        <small>Only pick one if this entry belongs to a different branch than the salesman is currently assigned to.</small>
                    </div>

                    <div className="form-group">
                        <label>Employee ID *</label>
                        <input
                            type="text"
                            value={formData.employeeId}
                            onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                            placeholder="Enter employee ID (e.g., B1010)"
                            required
                        />
                        <small>Enter the salesman's unique employee ID</small>
                    </div>

                    <div className="form-group">
                        <label>Total Sales *</label>
                        <input
                            type="number"
                            value={formData.totalSales}
                            onChange={(e) => setFormData({ ...formData, totalSales: e.target.value })}
                            placeholder="Enter total sales amount"
                            min="0"
                            step="0.01"
                            required
                        />
                        <small>Total sales achieved by the salesman for this month</small>
                    </div>

                    <div className="info-box">
                        <strong>Note:</strong> Only one report per salesman per day per branch is allowed.
                        By default the branch is found automatically from the salesman's Employee ID — set Branch above only to file this entry under a different one.
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={handleClose} className="btn-secondary" disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Uploading...' : 'Upload Report'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadReportModal;
